import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useI18n } from "../i18n";
import { useTheme } from "../ThemeContext";
import { Lock, Translate, SignOut, ChartPieSlice, UploadSimple, Crown, Sun, Moon, Info, ArrowLeft, CaretDown, List, X, ShieldCheck, FileText, EnvelopeSimple, ChatCircleDots } from "@phosphor-icons/react";

export const CAT_COLORS = {
  streaming: "#059669", palestra: "#10B981", software: "#0EA5E9",
  trasporti: "#0F172A", "food delivery": "#F59E0B", altro: "#94A3B8",
};

export const eur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));

export function FrumyMark({ className = "h-9 w-9", id = "fm" }) {
  return (
    <svg className={className} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <linearGradient id={`${id}-grad`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0B6E8C" />
          <stop offset="52%" stopColor="#0E8F8A" />
          <stop offset="100%" stopColor="#10B981" />
        </linearGradient>
        <linearGradient id={`${id}-shine`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="46%" stopColor="#ffffff" stopOpacity="0.06" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="38" height="38" rx="11" fill={`url(#${id}-grad)`} />
      <rect x="1" y="1" width="38" height="38" rx="11" fill={`url(#${id}-shine)`} />
      <rect x="1.5" y="1.5" width="37" height="37" rx="10.5" fill="none" stroke="#ffffff" strokeOpacity="0.35" strokeWidth="1" />
      {/* recurring-cycle double arrow (recurrence) */}
      <g transform="translate(9.6 9.6) scale(0.85)" stroke="#ffffff" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
        <path d="M21 3v5h-5" />
        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
        <path d="M3 21v-5h5" />
      </g>
      {/* euro (money) */}
      <text x="20" y="24.6" textAnchor="middle" fontFamily="'Cabinet Grotesk','Manrope',sans-serif" fontWeight="800" fontSize="12.5" fill="#ffffff">€</text>
    </svg>
  );
}

export function Logo({ onClick }) {
  return (
    <button onClick={onClick} data-testid="frumy-logo" className="flex items-center gap-2 group">
      <span className="transition-transform group-hover:scale-105">
        <FrumyMark className="h-9 w-9 drop-shadow-sm" />
      </span>
      <span className="font-display text-2xl font-extrabold tracking-tight text-[var(--heading)]">Frumy</span>
    </button>
  );
}

export function LangToggle() {
  const { lang, langs, setLanguage } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="lang-toggle"
        className="pill-btn flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]">
        <Translate size={16} aria-hidden />{lang.toUpperCase()}
        <CaretDown size={12} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </button>
      {open && (
        <div data-testid="lang-menu" className="absolute right-0 mt-1.5 py-1 rounded-xl border border-[var(--frumy-border)] bg-[var(--surface)] shadow-lg z-50 min-w-[92px]">
          {langs.map((l) => (
            <button key={l} data-testid={`lang-option-${l}`} onClick={() => { setLanguage(l); setOpen(false); }}
              className={`block w-full text-left px-3.5 py-1.5 text-sm font-semibold hover:bg-[var(--frumy-muted)] ${l === lang ? "text-[var(--frumy-green)]" : "text-[var(--text)]"}`}>
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Site-wide info links (privacy central to the product -> always in the header)
const INFO_LINKS = [
  { to: "/come-funziona", key: "about", testid: "nav-info-about", Icon: Info },
  { to: "/privacy", key: "privacyLink", testid: "nav-info-privacy", Icon: ShieldCheck },
  { to: "/termini", key: "termini", testid: "nav-info-termini", Icon: FileText },
  { to: "/contatti", key: "contatti", testid: "nav-info-contatti", Icon: EnvelopeSimple },
];

export function SiteNav() {
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <nav className="hidden md:flex items-center gap-4" data-testid="site-nav">
      {INFO_LINKS.map((l) => (
        <button key={l.to} data-testid={l.testid} onClick={() => nav(l.to)}
          className="text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--frumy-green)] transition-colors">
          {t(l.key)}
        </button>
      ))}
    </nav>
  );
}

export function MobileNavMenu() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative md:hidden" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-btn" aria-label={t("menu")}
        className="pill-btn grid place-items-center h-9 w-9 border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]">
        {open ? <X size={18} aria-hidden /> : <List size={18} aria-hidden />}
      </button>
      {open && (
        <div data-testid="mobile-menu" className="absolute right-0 mt-1.5 py-1 rounded-xl border border-[var(--frumy-border)] bg-[var(--surface)] shadow-lg z-50 min-w-[180px]">
          {INFO_LINKS.map((l) => (
            <button key={l.to} data-testid={`m-${l.testid}`} onClick={() => { nav(l.to); setOpen(false); }}
              className="flex items-center gap-2.5 w-full text-left px-4 py-2.5 text-sm font-semibold text-[var(--text)] hover:bg-[var(--frumy-muted)]">
              <l.Icon size={16} aria-hidden />{t(l.key)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const dark = theme === "dark";
  return (
    <button onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme"
      title={dark ? "Light mode" : "Dark mode"}
      className="pill-btn grid place-items-center h-9 w-9 border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]">
      {dark ? <Sun weight="fill" size={17} aria-hidden /> : <Moon weight="fill" size={17} aria-hidden />}
    </button>
  );
}

export function Header() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const link = (to, label, testid, Icon) => {
    const active = loc.pathname === to;
    return (
      <button data-testid={testid} onClick={() => nav(to)}
        className={`pill-btn flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-semibold ${active ? "bg-[var(--frumy-navy)] text-white" : "hover:bg-[var(--frumy-muted)]"}`}>
        <Icon size={16} aria-hidden />{label}
      </button>
    );
  };
  return (
    <header className="frumy-glass sticky top-0 z-40 border-b border-[var(--frumy-border)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Logo onClick={() => nav(user ? "/dashboard" : "/")} />
        <div className="flex items-center gap-2">
          {user && (
            <nav className="hidden sm:flex items-center gap-1.5">
              {link("/dashboard", t("dashboard"), "nav-dashboard", ChartPieSlice)}
              {link("/upload", t("upload"), "nav-upload", UploadSimple)}
              {link("/come-funziona", t("about"), "nav-about", Info)}
              {!user.is_pro && link("/pro", t("goPro"), "nav-pro", Crown)}
            </nav>
          )}
          <ThemeToggle />
          <LangToggle />
          {user && user.is_pro && (
            <span data-testid="pro-badge" className="hidden sm:flex items-center gap-1 pill-btn px-3 py-1.5 text-xs font-bold bg-[var(--frumy-lock)] text-white">
              <Crown weight="fill" size={14} aria-hidden />PRO
            </span>
          )}
          {user && (
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }}
              className="pill-btn grid place-items-center h-9 w-9 hover:bg-[var(--frumy-muted)]" title={t("logout")}>
              <SignOut size={18} aria-hidden />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

export function SecondaryHeader() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <header className="frumy-glass sticky top-0 z-40 border-b border-[var(--frumy-border)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Logo onClick={() => nav(user ? "/dashboard" : "/")} />
          <button data-testid="secondary-back-btn" onClick={() => nav(user ? "/dashboard" : "/")}
            className="pill-btn inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold border border-[var(--frumy-border)] hover:bg-[var(--frumy-muted)]">
            <ArrowLeft size={16} aria-hidden /><span className="hidden xs:inline sm:inline">{t("backHome")}</span>
          </button>
        </div>
        <div className="flex items-center gap-3 sm:gap-4">
          <SiteNav />
          <ThemeToggle />
          <LangToggle />
          <MobileNavMenu />
        </div>
      </div>
    </header>
  );
}

export function LockBadge({ label }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-[var(--frumy-lock)] border border-amber-200">
      <Lock weight="fill" size={13} aria-hidden />{label}
    </span>
  );
}

export function Footer({ onFeedback }) {
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <footer className="border-t border-[var(--frumy-border)] mt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="text-sm text-[var(--text-muted)]">© {new Date().getFullYear()} Frumy</span>
        <div className="flex items-center flex-wrap justify-center gap-4 sm:gap-5">
          <button data-testid="footer-about-link" onClick={() => nav("/come-funziona")}
            className="text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--frumy-green)] transition-colors">
            {t("about")}
          </button>
          <button data-testid="footer-privacy-link" onClick={() => nav("/privacy")}
            className="text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--frumy-green)] transition-colors">
            {t("privacyLink")}
          </button>
          <button data-testid="footer-termini-link" onClick={() => nav("/termini")}
            className="text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--frumy-green)] transition-colors">
            {t("termini")}
          </button>
          <button data-testid="footer-contatti-link" onClick={() => nav("/contatti")}
            className="text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--frumy-green)] transition-colors">
            {t("contatti")}
          </button>
          {onFeedback && (
            <button data-testid="footer-feedback-link" onClick={onFeedback}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--frumy-green)] transition-colors">
              <ChatCircleDots size={15} aria-hidden />{t("feedback")}
            </button>
          )}
        </div>
      </div>
    </footer>
  );
}
